import { AppStack } from 'component/navigation/app-stack';
import { ScreenErrorBoundary } from 'component/navigation/screen-error-boundary';
import type { SiloMember } from 'component/silo-member-context';
import { useSiloLanguage } from 'component/translation';
import { useSuspenseSiloMember } from 'data/me';
import { useSuspenseIsSuspended } from 'data/suspensions';
import { type RoutePath, router, useIsFocused, useLocalSearchParams } from 'expo-router';
import { useEffect, useEffectEvent } from 'react';
import { useGlobalHrefParams } from 'util/navigate';

export const unstable_settings = {
    screenErrorBoundary: ScreenErrorBoundary,
};

type GatePathname = '/[siloKey]/suspended' | '/[siloKey]/profile';

/**
 * Sends a suspended user to `/suspended` and a user without a profile to
 * profile setup, deciding before any page renders.
 *
 * The pages stay rendered under the pushed route, so the original page keeps
 * its state and Back returns to it.
 */
export default function AppLayout() {
    const { siloKey } = useLocalSearchParams<Extract<RoutePath, `/[siloKey]/${string}`>>();
    const locale = useSiloLanguage();
    const member = useSuspenseSiloMember({ siloKey, locale });
    const isSuspended = useSuspenseIsSuspended({ siloKey, profileKey: member?.profile.profileKey ?? null, locale });
    const pathname = getGatePathname({ isSuspended, member });
    usePushOnFocus({ pathname, siloKey });

    return <AppStack />;
}

function getGatePathname({ isSuspended, member }: { isSuspended: boolean; member: SiloMember | null }): GatePathname | null {
    if (isSuspended) {
        return '/[siloKey]/suspended';
    }

    if (member?.profile.hasProfile === false) {
        return '/[siloKey]/profile';
    }

    return null;
}

/**
 * Pushes a silo route whenever this route gains focus.
 *
 * Pushing, not replacing, keeps the current page in history so the pushed
 * route can go back.
 */
function usePushOnFocus({ pathname, siloKey }: { pathname: GatePathname | null; siloKey: string }) {
    const isFocused = useIsFocused();
    const globalParams = useGlobalHrefParams();

    // An effect event, so a change to the params doesn't push again.
    const push = useEffectEvent((gatePathname: GatePathname) => {
        router.push({ pathname: gatePathname, params: { ...globalParams, siloKey } });
    });

    useEffect(() => {
        if (isFocused && pathname) {
            push(pathname);
        }
    }, [isFocused, pathname]);
}
